import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  AfterViewInit,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef

} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators
} from "@angular/forms";
import {CustomerService} from "../../customer.service";
import {first, pairwise, startWith} from "rxjs/operators";
import {Subscription} from "rxjs";
import {HelperService} from "../../../../services/helper.service";
import {SharedService} from "../../../shared/shared.service";

import { NotOnlySpaces } from '../../../shared/CustomValidators/CustomNotOnlySpacesValidator';
import { EmailValidator } from '../../../shared/CustomValidators/CustomInvalidEmailValidator';
import {WebsiteURLValidator} from '../../../shared/CustomValidators/CustomWebsiteUrlValidator';
import {PhoneNumberValidator} from '../../../shared/CustomValidators/CustomInvalidPhoneNumberValidator';
import {FaxNumberValidator} from '../../../shared/CustomValidators/CustomInvalidFaxNumberValidator';

import {FormBuilder} from "@angular/forms";
import { ToasterService } from 'src/app/services/core/toaster.service';



@Component({
    selector: 'app-company-detail',
    templateUrl: './company-detail.component.html',
    styleUrls: ['./company-detail.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CompanyDetailComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CompanyDetailComponent),
            multi: true
        }
    ],
    standalone: false
})

export class CompanyDetailComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() model: any = {};
  @Input() form: any = {};
  @Input() isEdit: any = false;
  @Input() company_name: any;
  @Output() callback = new EventEmitter<any>(true);
  @Output() onInitForm = new EventEmitter<any>(true);
  urlPrefix = 'https://';
  public onModelValueChanged:any = {};
  public groups: string[] = [];
  //@ts-ignore
  public companyDetailFormSubscription: Subscription;
  panelOpenState = false;
  /** Expansion state of the Billing / Shipping address panels — bound with [(expanded)]. */
  billingExpanded = false;
  shippingExpanded = false;
  company_address: any = '';
  billing_address: any = '';
  shipping_address: any = '';

  billingAddressChangesFlag: boolean = false;
  shippingAddressChangesFlag: boolean = false;
  companyLogo: string = '';
  logoAttachmentId: number | null = null;
  // The logo drop zone is collapsed by default — it was the tallest thing on
  // this step by a wide margin. The button beside the "Add Logo" label opens
  // it, and any logo already on the record stays visible as the thumbnail
  // next to the "Customer Detail" heading, so nothing is hidden by collapsing.
  showLogoUpload: boolean = false;
  CONST_MODULE_ID: number = 13; // Customer module ID
  scrollMonitorInterval: any;
  private clickHandlerAttached: boolean = false;

  // Time options for start/end time dropdowns (24-hour format, 15-minute intervals)
  public timeOptions: string[] = [];
  public endTimeOptions: string[] = [];

  public companyDetailForm: UntypedFormGroup = this.initBasicForm();

  constructor(
    private customerService: CustomerService,
    private helperService: HelperService,
    private cd: ChangeDetectorRef,
    private fb: FormBuilder,
    private sharedService: SharedService,
    private toaster: ToasterService

  ) { }

  /**
   * Called from the stepper's "Next" click. An invalid Billing/Shipping address
   * (e.g. a bad ZIP) blocks matStepperNext, but if that panel is collapsed the
   * error is hidden and the user sees nothing. So expand each invalid panel that
   * is still collapsed and toast where to look.
   */
  /**
   * Fill status for the (collapsible) Billing/Shipping address panels, so the
   * header can show a status icon without the user having to expand it.
   * Emptiness is judged off the address sub-fields directly, not control.invalid,
   * since Shipping has no required validators and would otherwise always read "valid".
   */
  getAddressStatus(controlName: 'billing_address' | 'shipping_address'): 'empty' | 'error' | 'valid' {
    const control = this.companyDetailForm.get(controlName);
    const value = control?.value;
    const isEmpty = !value || !(value.address_line_1 || value.address_line_2 || value.city || value.state_id || value.zipcode);

    if (isEmpty) {
      return 'empty';
    }
    return control?.invalid ? 'error' : 'valid';
  }

  revealInvalidCollapsedPanels(): void {
    // Surface the errors (message shows on dirty || touched) once panels open.
    this.companyDetailForm.markAllAsTouched();

    const hidden: string[] = [];

    if (this.companyDetailForm.get('billing_address')?.invalid && !this.billingExpanded) {
      this.billingExpanded = true;
      hidden.push('Billing Address');
    }
    if (this.companyDetailForm.get('shipping_address')?.invalid && !this.shippingExpanded) {
      this.shippingExpanded = true;
      hidden.push('Shipping Address');
    }

    if (hidden.length) {
      this.toaster.showErrorMessage(
        `Please fix the highlighted errors in ${hidden.join(' and ')}.`
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.model = changes?.model?.currentValue;
    this.onModelValueChanged = this.model?.model?.detail;

    // When company_name changes (when moving from step 1 to step 2 OR when user changes company in edit mode)
    if (changes['company_name'] && changes['company_name'].currentValue) {
      const newCompanyName = changes['company_name'].currentValue;

      // Check if there's new business data from Google Places selection
      const businessDataStr = sessionStorage.getItem('selectedBusinessProfile');

      // Only auto-fill if there's sessionStorage data (means user selected from dropdown)
      if (businessDataStr) {
        const businessData = JSON.parse(businessDataStr);
        this.companyDetailForm.get('company_title')?.setValue(businessData.company_name);

        // Clear previous data first to avoid flickering
        this.clearFormData();

        // Load new business data
        setTimeout(() => {
          this.loadBusinessDataFromSession();
        }, 100);
      } else if (!this.isEdit) {
        // In create mode without sessionStorage, use company_name
        this.companyDetailForm.get('company_title')?.setValue(this.onModelValueChanged ? this.onModelValueChanged?.company_title : newCompanyName);
      }
    }
  }
  omit_special_char(event: KeyboardEvent): boolean {
    // const k = event.keyCode || event.which;
    // return (k >= 48 && k <= 57) || k == 8 || k == 32; // Allow numbers, backspace, and space
    return true;
  }
  ngOnInit(): void {
    this.initSubscriptions();
    this.initFormValues();
    this.getGroups();
    this.generateTimeOptions();

    // Load states for address dropdown
    this.sharedService.getStates([231, 38]);

    // Don't load business data here - wait for company_name to change
  }

  ngAfterViewInit(): void {
    // Manually connect dropzone click to file uploader button (only once)
    if (!this.clickHandlerAttached) {
      setTimeout(() => {
        const dropzone = document.querySelector('#customer_logo_upload') as HTMLElement;
        const uploaderButton = dropzone?.parentElement?.querySelector('.dx-fileuploader-button') as HTMLElement;

        if (dropzone && uploaderButton) {
          dropzone.style.cursor = 'pointer';
          dropzone.addEventListener('click', (e) => {
            uploaderButton.click();
          }, { once: false });
          this.clickHandlerAttached = true;
        }
      }, 1500);
    }

    // Start monitoring and auto-fixing scroll
    this.startScrollMonitor();
  }

  // Constantly monitor and restore scroll if blocked
  startScrollMonitor(): void {
    this.scrollMonitorInterval = setInterval(() => {
      // If no modal is open but body has overflow hidden, restore it
      const hasModal = document.querySelector('.cdk-overlay-backdrop-showing');
      const bodyHidden = window.getComputedStyle(document.body).overflow === 'hidden';

      if (!hasModal && bodyHidden) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.classList.remove('cdk-global-scrollblock');
      }
    }, 300);
  }
  preventSpace(event: KeyboardEvent) {
    if (event.keyCode === 32) {
      // event.preventDefault();
    }
  }
  initBasicForm(value: any = ''){
    return new UntypedFormGroup({

      company_title:new UntypedFormControl(null, [Validators.required,NotOnlySpaces()]),
      company_email:new UntypedFormControl(null,[EmailValidator()]),
      is_jobsite:new UntypedFormControl(0),
      phone: new UntypedFormControl(null, [PhoneNumberValidator()]),
      extension: new UntypedFormControl(null),
      secondary_phone:new UntypedFormControl(null, [PhoneNumberValidator()]),
      fax:new UntypedFormControl(null,[PhoneNumberValidator()]),
      website:new UntypedFormControl(null, [WebsiteURLValidator()]),
      group:new UntypedFormControl(null,[]),
      main_address: new UntypedFormControl(null),
      billing_address: new UntypedFormControl(null),
      shipping_address: new UntypedFormControl(null),
      logo_file: new UntypedFormControl(null),
      start_time: new UntypedFormControl(null),
      end_time: new UntypedFormControl(null),
    });
  }



  handleInput(event: KeyboardEvent): void{
    event.stopPropagation();
  }
  initFormValues(){
    this.companyDetailForm.get('is_jobsite')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.is_jobsite : '');
    this.companyDetailForm.get('company_title')?.setValue(this.onModelValueChanged ? this.onModelValueChanged?.company_title : this.company_name);
    this.companyDetailForm.get('company_email')?.setValue(this.onModelValueChanged? this.model?.model?.company_email : '');
    this.companyDetailForm.get('main_address')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.detail?.company_address : '');
    this.companyDetailForm.get('phone')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.phone : '');
    this.companyDetailForm.get('extension')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.extension : '');
    this.companyDetailForm.get('secondary_phone')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.secondary_phone : '');
    this.companyDetailForm.get('fax')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.fax : '');
    this.companyDetailForm.get('group')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.group : null);
    this.companyDetailForm.get('website')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.website : '');
    this.companyDetailForm.get('start_time')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.start_time : null);
    this.companyDetailForm.get('end_time')?.setValue(this.onModelValueChanged? this.onModelValueChanged?.end_time : null);
    // this.companyDetailForm.get('billing_address')?.setValue(this.onModelValueChanged?.billing_address);
    // this.companyDetailForm.get('shipping_address')?.setValue(this.onModelValueChanged?.shipping_address);

    this.company_address = this.changeAddressObject(this.model?.model?.detail.company_address);
    this.billing_address = this.changeAddressObject(this.model?.model?.detail.billing_address);
    this.shipping_address = this.changeAddressObject(this.model?.model?.detail.shipping_address);

    // Load logo and times if exists (for edit mode)
    if (this.isEdit) {
      // Load logo
      if (this.model?.model?.logo_attachment_id && this.model?.model?.logo_attachment) {
        this.logoAttachmentId = this.model.model.logo_attachment_id;
        this.companyLogo = this.model.model.logo_attachment.url || this.model.model.logo_attachment.attachment;
        this.companyDetailForm.get('logo_file')?.setValue(this.logoAttachmentId);
      }

      // Load start_time and end_time (they're in detail object)
      if (this.onModelValueChanged?.start_time) {
        this.companyDetailForm.get('start_time')?.setValue(this.onModelValueChanged.start_time);
        // Filter end time options based on loaded start time
        this.onStartTimeChange();
      }
      if (this.onModelValueChanged?.end_time) {
        this.companyDetailForm.get('end_time')?.setValue(this.onModelValueChanged.end_time);
      }
    }
  }

  getGroups = (queryParam:any = {}) => {
    this.customerService.getGroups(queryParam)
      .pipe(first())
      .subscribe(
        (data:any) => {
          this.groups = data.data.map((group:any)=>{
            return {id: group.id, name: group.name};
          });
        });
  }

  public changeAddressObject = (value:any) => {
    const obj = {
      address_line_1:value?.address_line_1,
      address_line_2:value?.address_line_2,
      city:value?.city,
      relations:{state:value?.state ? value?.state : value?.state_id},
      zipcode:value?.zipcode,
    }
    return obj;
  }
  public onTouched: () => void = () => {};

  writeValue(val: any): void {
    // console.log('writeValue', val);
    val && this.companyDetailForm.patchValue(val, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.companyDetailForm.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.companyDetailForm.disable() : this.companyDetailForm.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null{
    return this.companyDetailForm.valid ? null : { invalidForm: {valid: false, message: "mainInfoForm fields are invalid"}};
  }

  copyCompanyAddressToBillingAddress(){
    this.billingAddressChangesFlag = true;
    const address = this.companyDetailForm.get('main_address')?.value;
    this.billing_address = this.changeAddressObject(address);
    this.cd.detectChanges();
    this.billingAddressChangesFlag = false;
    // this.companyDetailForm.get('billing_address')?.setValue(address);
  }

  copyCompanyAddressToShippingAddress(){
    this.shippingAddressChangesFlag = true;
    const address = this.companyDetailForm.get('main_address')?.value;
    this.shipping_address = this.changeAddressObject(address);
    this.cd.detectChanges();
    this.shippingAddressChangesFlag = false;
  }

  onBlurWebsiteUrl(){
    const url = this.helperService.removeHttp(this.companyDetailForm.get('website')?.value)
    this.companyDetailForm.get('website')?.setValue(url);
  }

  initChildForm( formControlName: string, value: any){
    this.companyDetailForm.get(formControlName)?.setValue(value);
    // this.companyDetailForm.get('billing_address')?.setValue(value);
    this.onInitForm.emit(this.companyDetailForm);
  }

  initSubscriptions(){
    if (this.form){
      this.companyDetailFormSubscription = this.form.valueChanges
        .pipe(startWith(null), pairwise())
        .subscribe(([prev, next]: [any, any]) => {
          if (this.form.touched){
            this.touchFormControls();
          }
        });
    }

  }
  touchFormControls(){
    Object.keys(this.companyDetailForm.controls).forEach(key => {
      this.companyDetailForm.get(key)?.markAsTouched();
    });
  }
  // Show/hide the logo drop zone from the button beside the "Add Logo" label.
  toggleLogoUpload(): void {
    this.showLogoUpload = !this.showLogoUpload;
  }

  // Handle image load error
  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement) {
      this.companyLogo = '';
    }
  }

  // Handle logo attachment callback from app-attachment component
  onLogoAttached(event: any): void {
    if (event?.attachment?.attachment_id) {
      // User uploaded a logo - store the attachment ID
      this.logoAttachmentId = event.attachment.attachment_id;
      this.companyDetailForm.get('logo_file')?.setValue(this.logoAttachmentId);

      // Show uploaded logo in header (replace Clearbit logo)
      if (event?.attachment?.attach_file?.attachment || event?.attachment?.attach_file?.url) {
        this.companyLogo = event.attachment.attach_file.attachment || event.attachment.attach_file.url;
        this.cd.detectChanges();
      }
    }
  }

  // Handle logo deletion
  onLogoDeleted(event: any): void {
    // Clear attachment ID
    this.logoAttachmentId = null;
    this.companyDetailForm.get('logo_file')?.setValue(null);

    // Restore Clearbit logo if available
    const businessDataStr = sessionStorage.getItem('selectedBusinessProfile');
    if (businessDataStr) {
      const businessData = JSON.parse(businessDataStr);
      if (businessData.domain) {
        this.companyLogo = `https://logos-api.apistemic.com/domain:${businessData.domain}`;
      } else {
        this.companyLogo = '';
      }
    } else {
      this.companyLogo = '';
    }

    // Fix scroll issue after modal and toast close
    setTimeout(() => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      this.cd.detectChanges();
    }, 500);
  }

  // Generate time options (24-hour coverage with 12-hour AM/PM display, 15-minute intervals)
  generateTimeOptions(): void {
    const times: string[] = [];

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const period = hour < 12 ? 'AM' : 'PM';
        let displayHour = hour % 12;
        if (displayHour === 0) displayHour = 12; // 0 and 12 should display as 12
        const hourStr = displayHour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        times.push(`${hourStr}:${minuteStr} ${period}`);
      }
    }

    this.timeOptions = times;
    this.endTimeOptions = [...times];
  }

  // Filter end time options based on selected start time
  onStartTimeChange(): void {
    const startTime = this.companyDetailForm.get('start_time')?.value;

    if (startTime) {
      const startIndex = this.timeOptions.indexOf(startTime);
      // Show options after the selected start time (exclude the same time)
      this.endTimeOptions = this.timeOptions.slice(startIndex + 1);

      // If current end time is not valid anymore, reset it
      const currentEndTime = this.companyDetailForm.get('end_time')?.value;
      if (currentEndTime && !this.endTimeOptions.includes(currentEndTime)) {
        this.companyDetailForm.get('end_time')?.setValue(null);
      }
    } else {
      this.endTimeOptions = [...this.timeOptions];
    }
  }

  // Clear form data to prevent showing old business data
  clearFormData(): void {
    // Clear logo
    this.companyLogo = '';

    // Clear phone (use empty string to avoid phoneMask directive error)
    this.companyDetailForm.get('phone')?.setValue('', { emitEvent: false });

    // Clear website
    this.companyDetailForm.get('website')?.setValue('', { emitEvent: false });

    // Clear address
    this.companyDetailForm.get('main_address')?.setValue(null, { emitEvent: false });
    this.company_address = {
      address_line_1: '',
      address_line_2: '',
      city: '',
      relations: { state: null },
      zipcode: ''
    };

    this.cd.detectChanges();
  }

  // Extract only domain from URL (remove path and query params)
  extractDomainOnly(url: string): string {
    try {
      // Create URL object to parse the URL
      const urlObj = new URL(url);
      // Return only the hostname (e.g., www.walmart.com or walmart.com)
      return urlObj.hostname;
    } catch (error) {
      // If URL parsing fails, try manual extraction
      // Remove http://, https://, and everything after the domain
      let domain = url.replace(/^(?:https?:\/\/)?/i, ''); // Remove protocol
      domain = domain.split('/')[0]; // Get only domain part before first /
      domain = domain.split('?')[0]; // Remove query params if any
      return domain;
    }
  }

  // Load business data from sessionStorage and auto-fill form
  loadBusinessDataFromSession(): void {
    const businessDataStr = sessionStorage.getItem('selectedBusinessProfile');

    if (!businessDataStr) {
      return;
    }

    try {
      const businessData = JSON.parse(businessDataStr);

      // Fetch and set company logo from Clearbit using domain
      if (businessData.domain) {
        this.companyLogo = `https://logos-api.apistemic.com/domain:${businessData.domain}`;
        this.cd.detectChanges();
      }

      // Auto-fill phone number
      if (businessData.phone) {
        this.companyDetailForm.get('phone')?.setValue(businessData.phone);
      }

      // Auto-fill website (extract only domain, no path or query params)
      if (businessData.website) {
        const cleanWebsite = this.extractDomainOnly(businessData.website);
        this.companyDetailForm.get('website')?.setValue(cleanWebsite);
      }

      // Auto-fill business hours
      if (businessData.start_time) {
        this.companyDetailForm.get('start_time')?.setValue(businessData.start_time);
      }
      if (businessData.end_time) {
        this.companyDetailForm.get('end_time')?.setValue(businessData.end_time);
      }

      // Auto-fill address fields
      if (businessData.address_line_1 || businessData.city || businessData.state || businessData.zipcode) {
        // Find state by full name (Google gives us "Wisconsin", not "WI")
        let state = null;
        if (businessData.state && this.sharedService.states) {
          state = this.sharedService.states.find(
            (s: any) => s.name === businessData.state
          );
        }

        const addressData = {
          address_line_1: businessData.address_line_1 || null,
          address_line_2: null,
          city: businessData.city || null,
          state_id: state || null,
          zipcode: businessData.zipcode || null
        };

        // Set main address form control
        this.companyDetailForm.get('main_address')?.setValue(addressData, { emitEvent: true });

        // Update the address display model for the address component
        this.company_address = {
          address_line_1: businessData.address_line_1 || '',
          address_line_2: '',
          city: businessData.city || '',
          relations: { state: state },
          zipcode: businessData.zipcode || ''
        };

        this.cd.detectChanges();
      }

    } catch (error) {
      console.error('Error loading business data from session:', error);
    }
  }

  ngOnDestroy(){
    // prevent memory leak when component destroyed
    this.companyDetailFormSubscription?.unsubscribe();

    // Clear scroll monitor interval
    if (this.scrollMonitorInterval) {
      clearInterval(this.scrollMonitorInterval);
    }
  }
}
